import React, { useEffect, useContext, useState } from 'react'
import { Box, CircularProgress, Icon, IconButton, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { authContext } from 'contexts/AuthContext'
import Bugsnag from '@bugsnag/js'
import { useNavigate } from 'react-router-dom'
import PersonalityFit from '../../../assets/icons/svg/work-persona-fit.svg'

// Local import
import PersonalTab from '../tabs/PersonalTab'
import EmploymentTab from '../tabs/EmploymentTab'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import BankTab from '../tabs/BankTab'
import TaxTab from '../tabs/TaxTab'
import AllocationTab from '../tabs/AllocationTab'
import AdxDropzoneProfileImage from 'components/reusable/AdxDropzoneProfileImage'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import {
  getProgressStaff,
  getPersonalData,
  getMonthlyPayslip,
  displayIndividualPayslip,
  getYearlyEAForm,
  displayIndividualEAForm,
  exportActiveStaffEmployeeFile,
  cancelEmployeeTermination,
} from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'
import AdxAccordion from 'components/custom/AdxAccordion'
import { ArrowOutward, BarChart, ChevronRight } from '@mui/icons-material'
import AdxPopper from 'components/reusable/AdxPopper'
import AdxToolTip from 'components/custom/AdxToolTip'
import { downloadResponseFile, notifyDownloadError } from 'services/helper/fileDownload'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { toast } from 'react-toastify'

const ManageEmployeeFile = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { auth } = useContext(authContext)
  const params = useParams()
  let currentWindow = window.location.pathname
  let windowCheckEmployeeFile = currentWindow.substring(1, 12) !== 'app/profile'
  const [selectedPayslip, setSelectPayslip] = useState({})
  const [selectedEAForm, setSelectEAForm] = useState('')
  const location = useLocation()

  const [isLoadingPayslip, setIsLoadingPayslip] = useState(false)
  const [isLoadingEA, setIsLoadingEA] = useState(false)
  const [exportingFormat, setExportingFormat] = useState(null)
  const [isCancelTerminationOpen, setCancelTerminationOpen] = useState(false)
  const isExporting = Boolean(exportingFormat)

  const { data: employee } = useQuery(['detailedEmployeeData', params.id], async () => {
    const { data } = await getProgressStaff(params.id)

    return data
  })

  // to get the status, eg active, terminate
  const { data: personal } = useQuery(['employeePersonalData', params.id], async () => {
    const { data } = await getPersonalData(params.id)
    return data
  })

  // to get the month for each individual employee payslip
  const { data: monthlyPayslip } = useQuery(
    'getMonthlyPayslip',
    async () => {
      const { data } = await getMonthlyPayslip()
      const newArr = data?.map((item) => {
        const { month, ...rest } = item
        switch (month) {
          case '1':
            return { month: 'January', ...rest }
          case '2':
            return { month: 'February', ...rest }
          case '3':
            return { month: 'March', ...rest }
          case '4':
            return { month: 'April', ...rest }
          case '5':
            return { month: 'May', ...rest }
          case '6':
            return { month: 'June', ...rest }
          case '7':
            return { month: 'July', ...rest }
          case '8':
            return { month: 'August', ...rest }
          case '9':
            return { month: 'September', ...rest }
          case '10':
            return { month: 'October', ...rest }
          case '11':
            return { month: 'November', ...rest }
          case '12':
            return { month: 'December', ...rest }
          default:
            return null
        }
      })
      return newArr
    },
    {
      onError: (error) => {
        if (error.status === 422) {
          console.log(error)
        } else {
          Bugsnag.notify(error.data)
        }
      },
    }
  )

  useEffect(() => {
    if (monthlyPayslip) {
      setSelectPayslip(monthlyPayslip[0])
    }
  }, [monthlyPayslip])

  // post request for viewing payslip
  const { mutate: mutatePayslip } = useMutation((payload) => displayIndividualPayslip(payload), {
    onSuccess: (res) => {
      setIsLoadingPayslip(false)
      window.open(res?.data?.data?.attachment, '_blank')
    },
    onError: (error) => {
      setIsLoadingPayslip(false)
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const viewPayslip = () => {
    setIsLoadingPayslip(true)
    mutatePayslip({
      time_id: selectedPayslip?.time_id,
    })
  }

  // // get yearly EA Form
  const { data: yearlyEAForm } = useQuery(
    'getYearlyEAForm',
    async () => {
      const { data } = await getYearlyEAForm()
      return data
    },
    {
      onError: (error) => {
        if (error.status === 422) {
          console.log(error)
        } else {
          Bugsnag.notify(error.data)
        }
      },
    }
  )

  useEffect(() => {
    if (yearlyEAForm?.length > 0) {
      setSelectEAForm(yearlyEAForm?.[0].year)
    }
  }, [yearlyEAForm])

  // post request for viewing EA Form
  const { mutate: mutateEAForm } = useMutation((payload) => displayIndividualEAForm(payload), {
    onSuccess: (res) => {
      setIsLoadingEA(false)
      const file = new Blob([res.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(file)
      window.open(url, '_blank')
    },
    onError: (error) => {
      setIsLoadingEA(false)
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const viewEAForm = () => {
    setIsLoadingEA(true)
    mutateEAForm(selectedEAForm)
  }

  const sanitizeFileName = (value = 'staff') => {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  const handleExportEmployeeFile = async (format) => {
    const exportConfig = {
      xlsx: {
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extension: 'xlsx',
        successMessage: 'Employee file XLSX downloaded.',
        errorMessage: 'Unable to export employee file XLSX. Please try again.',
      },
      pdf: {
        contentType: 'application/pdf',
        extension: 'pdf',
        successMessage: 'Employee file PDF downloaded.',
        errorMessage: 'Unable to export employee file PDF. Please try again.',
      },
    }
    const config = exportConfig[format]

    try {
      setExportingFormat(format)

      const fileName = `${sanitizeFileName(employee?.data?.name)}-employee-file.${
        config.extension
      }`
      const response = await exportActiveStaffEmployeeFile(format, [params.id])

      downloadResponseFile(response, {
        fileName,
        contentType: config.contentType,
        successMessage: config.successMessage,
      })
    } catch (error) {
      notifyDownloadError(config.errorMessage)
    } finally {
      setExportingFormat(null)
    }
  }

  const personalDetails = (employee?.data?.personal_count / employee?.data?.personal_total) * 100

  const employmentDetails =
    (employee?.data?.employment_count / employee?.data?.employment_total) * 100

  const payrollDetails =
    (employee?.data?.bank_statutory_count / employee?.data?.bank_statutory_total) * 100

  const householdDetails = (employee?.data?.tax_count / employee?.data?.tax_total) * 100
  const isActiveStaff = personal?.data?.employment_status === 1
  const isTerminatedStaff = personal?.data?.employment_status === 2
  const terminationScheduled = Boolean(personal?.data?.termination_scheduled)
  const terminationCanCancel = Boolean(personal?.data?.termination_can_cancel)
  const terminationPendingFinalization = Boolean(
    personal?.data?.termination_pending_finalization
  )
  const offboardingDate = personal?.data?.employment_last_day
  const formattedOffboardingDate = offboardingDate
    ? offboardingDate.toString().substring(0, 10).split('-').reverse().join('/')
    : ''

  const cancelTerminationMutation = useMutation(() => cancelEmployeeTermination(params.id), {
    onSuccess: () => {
      setCancelTerminationOpen(false)
      queryClient.invalidateQueries(['employeePersonalData', params.id])
      toast.success('Staff termination schedule cancelled.')
    },
    onError: (error) => {
      const message =
        error?.data?.message ||
        error?.response?.data?.message ||
        'Unable to cancel the termination schedule.'
      toast.error(message)
    },
  })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (auth?.data?.staff?.id) {
      if (parseInt(params.id) !== auth?.data?.staff?.id && !windowCheckEmployeeFile) {
        window.open('/app', '_self')
      }
    }
  }, [auth]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer
      withHeader
      headerTitle={
        windowCheckEmployeeFile ? (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Typography
              onClick={() => navigate(-1)}
              sx={{ cursor: 'pointer', color: 'alternate.main' }}
            >
              Employee
            </Typography>
            <ChevronRight />
            <Typography>{employee?.data?.name}</Typography>
          </Box>
        ) : (
          'Profile'
        )
      }
      stickyHeader
      sx={{
        height: { xs: 'auto', lg: '100vh' },
        minHeight: '100vh',
        overflowX: 'hidden',
        overflowY: { xs: 'auto', lg: 'hidden' },
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        {windowCheckEmployeeFile && (
          <Box display="flex" gap={2.375} alignItems="center" width={1} justifyContent={'end'}>
            <AdxButton
              onClick={() =>
                navigate(
                  `/app/management-centre/staff-management/employee-file/manages/edit-all/${params.id}`,
                  {
                    state: {
                      backgroundLocation: location,
                    },
                  }
                )
              }
            >
              Edit all
            </AdxButton>
            {(isActiveStaff || isTerminatedStaff) && (
              <AdxPopper
                sx={{ mt: 2, minWidth: 190 }}
                placement="bottom-start"
                disabled={isExporting}
                linePlacement={[2]}
                customButton={
                  <AdxToolTip title="Employee actions">
                    <span>
                      <IconButton
                        aria-label="Employee actions"
                        size="small"
                        disabled={isExporting}
                        sx={{
                          color: 'light-gray-text.main',
                          '&:hover': {
                            bgcolor: 'gray-background.main',
                          },
                        }}
                      >
                        {isExporting ? (
                          <CircularProgress color="alternate" size={18} />
                        ) : (
                          <Icon>more_horiz</Icon>
                        )}
                      </IconButton>
                    </span>
                  </AdxToolTip>
                }
              >
                {isActiveStaff && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={isExporting ? undefined : () => handleExportEmployeeFile('xlsx')}
                  >
                    <Icon sx={{ color: 'alternate.main' }}>download</Icon>
                    <Typography>
                      {exportingFormat === 'xlsx' ? 'Exporting XLSX...' : 'Export as XLSX'}
                    </Typography>
                  </Box>
                )}
                {isActiveStaff && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={isExporting ? undefined : () => handleExportEmployeeFile('pdf')}
                  >
                    <Icon sx={{ color: 'light-gray-text.main' }}>picture_as_pdf</Icon>
                    <Typography>
                      {exportingFormat === 'pdf' ? 'Exporting PDF...' : 'Export as PDF'}
                    </Typography>
                  </Box>
                )}
                {isTerminatedStaff && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={() =>
                      navigate(
                        '/app/management-centre/staff-management/employee-file/manages/rehire',
                        {
                          state: {
                            backgroundLocation: location,
                            employee: employee?.data,
                            employeeID: params.id,
                          },
                        }
                      )
                    }
                  >
                    <Icon sx={{ color: 'alternate.main' }}>person_add</Icon>
                    <Typography>Rehire Staff</Typography>
                  </Box>
                )}
                {isActiveStaff && !terminationScheduled && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={() =>
                      navigate(
                        '/app/management-centre/staff-management/employee-file/manages/terminate',
                        {
                          state: {
                            backgroundLocation: location,
                            employee: employee?.data,
                            employeeID: params.id,
                          },
                        }
                      )
                    }
                  >
                    <Icon sx={{ color: 'error.main' }}>person_off</Icon>
                    <Typography>Terminate Staff</Typography>
                  </Box>
                )}
                {terminationScheduled && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'default' }}>
                    <Icon sx={{ color: 'warning.main' }}>event_busy</Icon>
                    <Box>
                      <Typography variant="body2">
                        {terminationPendingFinalization
                          ? 'Termination pending finalization'
                          : 'Termination scheduled'}
                      </Typography>
                      <Typography variant="caption" color="light-gray-text.main">
                        Last working day: {formattedOffboardingDate}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {terminationScheduled && terminationCanCancel && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={() => setCancelTerminationOpen(true)}
                  >
                    <Icon sx={{ color: 'error.main' }}>event_available</Icon>
                    <Typography>Cancel Termination</Typography>
                  </Box>
                )}
              </AdxPopper>
            )}
          </Box>
        )}
        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid item xs={12} lg={3}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden' }}
            >
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <AdxDropzoneProfileImage
                  employee={employee}
                  windowCheckEmployeeFile={windowCheckEmployeeFile}
                />
                {employee && (
                  <>
                    <Typography
                      fontWeight={500}
                      color="dark-text.main"
                      textAlign={'center'}
                      mb={0.25}
                    >
                      {employee?.data?.name ? employee?.data?.name : ''}
                    </Typography>
                    <Typography fontWeight={400} color="light-gray-text.main" textAlign={'center'}>
                      {employee?.data?.designation && employee?.data?.department
                        ? employee?.data?.designation + ', ' + employee?.data?.department
                        : employee?.data?.designation
                        ? employee?.data?.designation
                        : employee?.data?.department
                        ? employee?.data?.department
                        : ''}
                    </Typography>
                    {personal?.data?.employment_status && (
                      <AdxStatusBar
                        status={{
                          text:
                            personal?.data?.employment_status === 1
                              ? 'Active'
                              : personal?.data?.employment_status === 2
                              ? 'Terminated'
                              : null,
                          color:
                            personal?.data?.employment_status === 1
                              ? 'alternate.light'
                              : personal?.data?.employment_status === 2
                              ? 'light-gray-text.main'
                              : null,
                          bgColor:
                            personal?.data?.employment_status === 1
                              ? 'alternate.main'
                              : personal?.data?.employment_status === 2
                              ? 'gray-line.main'
                              : null,
                        }}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </>
                )}
              </Box>
              <Box
                sx={{
                  overflowY: 'auto',
                  maxHeight: windowCheckEmployeeFile
                    ? { lg: 'calc(100vh - 500px)' }
                    : { lg: 'calc(100vh - 407px)' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
                className="scrollbar"
              >
                {!employee?.data?.complete && (
                  <Box
                    p={2}
                    sx={{
                      backgroundColor: 'white.main',
                      border: 1,
                      borderRadius: '4.5px',
                      borderColor: 'gray-line.main',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        Onboarding Progress
                      </Typography>
                      {employee?.data?.complete ? (
                        <AdxStatusBar
                          status={{
                            text: 'Complete',
                            color: 'alternate.main',
                            bgColor: 'alternate.light',
                          }}
                        />
                      ) : (
                        <AdxStatusBar
                          status={{
                            text: 'Incomplete',
                            color: 'yellow-01.main',
                            bgColor: 'light-yellow-01.main',
                          }}
                        />
                      )}
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Personal Details</Typography>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        {employee?.data?.personal_count ? employee?.data?.personal_count : 0}/
                        {employee?.data?.personal_total ? employee?.data?.personal_total : 0}
                      </Typography>
                    </Box>
                    <Box mb={2.5} pt={0.75}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          personalDetails ? (personalDetails > 100 ? 100 : personalDetails) : 0
                        }
                        sx={{
                          height: 8,
                          borderRadius: 6,
                          [`& .${linearProgressClasses.bar}`]: {
                            borderRadius: 6,
                            backgroundColor:
                              personalDetails >= 100 ? 'alternate.main' : 'warning.main',
                          },
                          backgroundColor: 'gray-inactive-button.main',
                          width: '100%',
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Employment Details</Typography>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        {employee?.data?.employment_count ? employee?.data?.employment_count : 0}/
                        {employee?.data?.employment_total ? employee?.data?.employment_total : 0}
                      </Typography>
                    </Box>
                    <Box mb={2.5} pt={0.75}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          employmentDetails
                            ? employmentDetails > 100
                              ? 100
                              : employmentDetails
                            : 0
                        }
                        sx={{
                          height: 8,
                          borderRadius: 6,
                          [`& .${linearProgressClasses.bar}`]: {
                            borderRadius: 6,
                            backgroundColor:
                              employmentDetails >= 100 ? 'alternate.main' : 'warning.main',
                          },
                          backgroundColor: 'gray-inactive-button.main',
                          width: '100%',
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Payroll Details</Typography>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        {employee?.data?.bank_statutory_count
                          ? employee?.data?.bank_statutory_count
                          : 0}
                        /
                        {employee?.data?.bank_statutory_total
                          ? employee?.data?.bank_statutory_total
                          : 0}
                      </Typography>
                    </Box>
                    <Box mb={2.5} pt={0.75}>
                      <LinearProgress
                        variant="determinate"
                        value={payrollDetails ? (payrollDetails > 100 ? 100 : payrollDetails) : 0}
                        sx={{
                          height: 8,
                          borderRadius: 6,
                          [`& .${linearProgressClasses.bar}`]: {
                            borderRadius: 6,
                            backgroundColor:
                              payrollDetails >= 100 ? 'alternate.main' : 'warning.main',
                          },
                          backgroundColor: 'gray-inactive-button.main',
                          width: '100%',
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Household Details</Typography>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        {employee?.data?.tax_count ? employee?.data?.tax_count : 0}/
                        {employee?.data?.tax_total ? employee?.data?.tax_total : 0}
                      </Typography>
                    </Box>
                    <Box pt={0.75}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          householdDetails ? (householdDetails > 100 ? 100 : householdDetails) : 0
                        }
                        sx={{
                          height: 8,
                          borderRadius: 6,
                          [`& .${linearProgressClasses.bar}`]: {
                            borderRadius: 6,
                            backgroundColor:
                              householdDetails >= 100 ? 'alternate.main' : 'warning.main',
                          },
                          backgroundColor: 'gray-inactive-button.main',
                          width: '100%',
                        }}
                      />
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {!windowCheckEmployeeFile && (
                    <AdxAccordion icon="feed" title="Payslip">
                      <Typography sx={{ textAlign: 'start', color: 'light-gray-text.main' }}>
                        View and download your latest payslip
                      </Typography>
                      <Box>
                        <AdxDropdown
                          value={selectedPayslip}
                          onChange={(e) => setSelectPayslip(e.target.value)}
                          renderValue={(selected) => {
                            return <Typography>{`${selected?.month} ${selected?.year}`}</Typography>
                          }}
                        >
                          {selectedPayslip !== undefined ? (
                            monthlyPayslip?.map((payslip, index) => (
                              <AdxMenuItem
                                key={index}
                                value={payslip}
                                selectedvalue={selectedPayslip}
                              >{`${payslip?.month} ${payslip?.year}`}</AdxMenuItem>
                            ))
                          ) : (
                            <Box px={2} py={1}>
                              <Typography color="light-gray-text.main">
                                No payslip generated yet.
                              </Typography>
                            </Box>
                          )}
                        </AdxDropdown>
                        <AdxButton
                          variant={'contained'}
                          disabled={isLoadingPayslip || selectedPayslip === undefined}
                          onClick={viewPayslip}
                          fullWidth
                          sx={{ mt: 1 }}
                        >
                          {isLoadingPayslip ? 'Redirecting...' : 'View'}
                        </AdxButton>
                      </Box>
                    </AdxAccordion>
                  )}
                  {!windowCheckEmployeeFile && (
                    <AdxAccordion icon="feed" title="EA Form">
                      <Typography sx={{ textAlign: 'start', color: 'light-gray-text.main' }}>
                        View and download your yearly EA Form
                      </Typography>
                      <Box>
                        <AdxDropdown
                          value={selectedEAForm}
                          onChange={(e) => setSelectEAForm(e.target.value)}
                          renderValue={(selected) => {
                            return <Typography>{selected}</Typography>
                          }}
                        >
                          {selectedEAForm.length > 0 ? (
                            yearlyEAForm?.map((item, index) => (
                              <AdxMenuItem
                                key={index}
                                value={item?.year}
                                selectedvalue={selectedEAForm}
                              >
                                {item?.year}
                              </AdxMenuItem>
                            ))
                          ) : (
                            <Box px={2} py={1}>
                              <Typography color="light-gray-text.main">
                                No EA Form generated yet.
                              </Typography>
                            </Box>
                          )}
                        </AdxDropdown>
                        <AdxButton
                          variant={'contained'}
                          disabled={isLoadingEA || selectedEAForm.length === 0}
                          onClick={viewEAForm}
                          fullWidth
                          sx={{ mt: 1 }}
                        >
                          {isLoadingEA ? 'Redirecting...' : 'View'}
                        </AdxButton>
                      </Box>
                    </AdxAccordion>
                  )}
                  {!windowCheckEmployeeFile && (
                    <Link
                      to={'/app/performance/assessment'}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Box
                        display={'flex'}
                        height={'69px'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        p={2}
                        bgcolor={'background.main'}
                        border={1}
                        borderColor={'grayscale.line-light'}
                        borderRadius={'4.5px'}
                      >
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          justifyContent={'start'}
                          gap={2}
                        >
                          <Box
                            sx={{
                              bgcolor: 'alternate.light',
                              width: '45px',
                              height: '45px',
                              minHeight: '45px',
                              minWidth: '45px',
                              borderRadius: '50%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <img src={PersonalityFit} alt="icon" width={25} height={25} />
                          </Box>

                          <Typography variant="h4">Work Personality Fit</Typography>
                        </Box>
                        <ArrowOutward />
                      </Box>
                    </Link>
                  )}
                  {!windowCheckEmployeeFile && (
                    <Link
                      to={'/app/performance/appraisal'}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Box
                        display={'flex'}
                        height={'69px'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        p={2}
                        bgcolor={'background.main'}
                        border={1}
                        borderColor={'grayscale.line-light'}
                        borderRadius={'4.5px'}
                      >
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          justifyContent={'start'}
                          gap={2}
                        >
                          <Box
                            sx={{
                              bgcolor: 'alternate.light',
                              width: '45px',
                              height: '45px',
                              borderRadius: '50%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: 'alternate.main',
                            }}
                          >
                            <BarChart />
                          </Box>
                          <Typography variant="h4">Appraisal</Typography>
                        </Box>
                        <ArrowOutward />
                      </Box>
                    </Link>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            lg={9}
            sx={{
              maxHeight: windowCheckEmployeeFile ? 'calc(100vh - 185px)' : 'calc(100vh - 123px)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'white.main',
                border: 1,
                borderColor: 'gray-line.main',
                borderRadius: '4.5px',
                height: '100%', // Ensure full height
                width: '100%', // Ensure full width
              }}
            >
              <AdxTabsBadge
                scrollButtons={false}
                initialValue={0}
                sxPanel={{
                  px: 2,
                }}
              >
                <AdxTabsPanel label="1. Personal">
                  <PersonalTab
                    windowCheckEmployeeFile={windowCheckEmployeeFile}
                    employeeID={params.id}
                  />
                </AdxTabsPanel>
                <AdxTabsPanel label="2. Employment">
                  <EmploymentTab
                    windowCheckEmployeeFile={windowCheckEmployeeFile}
                    employeeID={params.id}
                  />
                </AdxTabsPanel>
                <AdxTabsPanel label={`3. Bank & Statutory`}>
                  <BankTab
                    windowCheckEmployeeFile={windowCheckEmployeeFile}
                    employeeID={params.id}
                  />
                </AdxTabsPanel>
                <AdxTabsPanel label="4. Tax">
                  <TaxTab
                    windowCheckEmployeeFile={windowCheckEmployeeFile}
                    employeeID={params.id}
                  />
                </AdxTabsPanel>
                {windowCheckEmployeeFile && (
                  <AdxTabsPanel label="5. Allocation">
                    <AllocationTab />
                  </AdxTabsPanel>
                )}
              </AdxTabsBadge>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <AdxInfoModal
        variant="warning"
        open={isCancelTerminationOpen}
        onClose={() => setCancelTerminationOpen(false)}
        muiIcon="event_available"
        iconColor="warning.main"
        title="Cancel Staff Termination?"
        subtitle={`${
          employee?.data?.name || 'This staff member'
        } will remain active and the ${formattedOffboardingDate} offboarding date will be removed.`}
        footerButtons
        firstBtnLabel="Confirm Cancellation"
        firstBtnHandle={() => cancelTerminationMutation.mutate()}
        firstBtnLoading={cancelTerminationMutation.isLoading}
        firstBtnDisabled={cancelTerminationMutation.isLoading}
        secondBtnLabel="Back"
        secondBtnHandle={() => setCancelTerminationOpen(false)}
        secondBtnDisabled={cancelTerminationMutation.isLoading}
      />
    </DashboardContainer>
  )
}

export default ManageEmployeeFile
